import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Icon } from '@/components/forge/Icon'
import { IconButton } from '@/components/forge/ForgeButton'
import { activeTrigger, matchModules, mentionsInText, searchRecords } from '@/features/copilot/mentions'
import { cn } from '@/lib/cn'

/** How long typing has to pause before `#` searches. Each search is several list reads. */
const SEARCH_DELAY_MS = 250

/**
 * The dock's composer: the context chip, quoted passages, and a field that
 * understands `@` and `#`.
 *
 * ── The context chip ──────────────────────────────────────────────────────
 * Prefilled by the shell from the screen underneath — `@Anomalies`, `#ANM-234` —
 * and sent as a mention with the question unless the reader removes it. It sits in
 * the box rather than in the text because it is not something they typed, and
 * removing it should not mean editing their sentence.
 *
 * ── Typed mentions ────────────────────────────────────────────────────────
 * `@` offers the modules; `#` searches records across them. A pick is inserted
 * into the text as the reader will see it in the transcript, and remembered beside
 * it; deleting it from the text is how it is removed.
 *
 * Reset by its parent's `key` when the screen changes — a new screen has its own
 * context, and a half-typed question about the last one is not about this one.
 *
 * @param {object} props
 * @param {{current: HTMLInputElement|null}} props.inputRef focused when a quote arrives
 * @param {boolean} props.disabled
 * @param {string} props.placeholder
 * @param {string|null} props.contextLabel the chip's text, or null for no chip
 * @param {() => void} props.onClearContext
 * @param {React.ReactNode} [props.attachments] the quoted passages, rendered above the field
 * @param {(text: string, mentions: {module: string, module_id: string|null}[]) => void} props.onSend
 */
export function CopilotComposer({ inputRef, disabled, placeholder, contextLabel, onClearContext, attachments, onSend }) {
  const { t } = useTranslation(['app', 'common'])
  const listId = useId()
  const [draft, setDraft] = useState('')
  const [tracked, setTracked] = useState([])
  const [trigger, setTrigger] = useState(null)
  const [active, setActive] = useState(0)
  const [search, setSearch] = useState('')
  const timer = useRef(null)

  const records = useQuery({
    queryKey: ['copilot', 'mention-search', search],
    queryFn: ({ signal }) => searchRecords(search, signal),
    enabled: trigger?.kind === '#' && search.length > 0,
    staleTime: 30_000,
  })

  const moduleLabel = (module) => t(`dock.modules.${module}`)

  /* What the menu offers right now. A `#` result is only shown for the query it
     was fetched for: while typing runs ahead of the debounce, the previous query's
     rows would be offered against text they no longer match. */
  const options =
    trigger?.kind === '@'
      ? matchModules(trigger.query, moduleLabel)
      : trigger?.kind === '#' && trigger.query && search === trigger.query
        ? (records.data ?? [])
        : []
  const menuOpen = Boolean(trigger) && !disabled
  const highlighted = Math.min(active, Math.max(options.length - 1, 0))

  const track = (value, caret) => {
    const next = activeTrigger(value, caret)
    setTrigger(next)
    setActive(0)
    clearTimeout(timer.current)
    if (next?.kind === '#') timer.current = setTimeout(() => setSearch(next.query), SEARCH_DELAY_MS)
  }

  const onChange = (event) => {
    setDraft(event.target.value)
    track(event.target.value, event.target.selectionStart ?? event.target.value.length)
  }

  const pick = (option) => {
    if (!trigger || !option) return
    const token = `${option.module_id ? '#' : '@'}${option.label} `
    const next = draft.slice(0, trigger.start) + token + draft.slice(trigger.end).replace(/^\s+/, '')
    const caret = trigger.start + token.length
    setDraft(next)
    setTracked((current) => [...current, { module: option.module, module_id: option.module_id, label: option.label }])
    setTrigger(null)
    requestAnimationFrame(() => {
      inputRef.current?.focus()
      inputRef.current?.setSelectionRange(caret, caret)
    })
  }

  const onKeyDown = (event) => {
    if (!menuOpen) return
    if (event.key === 'Escape') {
      event.preventDefault()
      setTrigger(null)
      return
    }
    if (!options.length) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((highlighted + step + options.length) % options.length)
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      pick(options[highlighted])
    }
  }

  const submit = (event) => {
    event.preventDefault()
    const text = draft.trim()
    if (disabled || !text) return
    const mentions = mentionsInText(draft, tracked).map(({ module, module_id }) => ({ module, module_id }))
    onSend(text, mentions)
    setDraft('')
    setTracked([])
    setTrigger(null)
  }

  const menuState =
    trigger?.kind !== '#'
      ? null
      : !trigger.query
        ? t('dock.mention.typeToSearch')
        : search !== trigger.query || records.isFetching
          ? t('dock.mention.searching')
          : options.length === 0
            ? t('dock.mention.noMatches')
            : null

  return (
    <div className="relative">
      {/* ── The mention menu ────────────────────────────────────────────
          Above the box, not below it: the composer sits on the dock's bottom edge
          and there is nothing below it to open into. `bg-overlay`, not `bg-card`:
          the card surface is 6% white in dark mode, and the transcript showed
          straight through the menu. */}
      {menuOpen ? (
        <div className="absolute inset-x-0 bottom-full z-20 mb-1.5 max-h-64 overflow-y-auto rounded-sm border border-hairline bg-overlay p-1 shadow-lift">
          <p className="m-0 px-2 pt-1 pb-1.5 text-micro uppercase tracking-wide text-ink-dim">
            {trigger.kind === '@' ? t('dock.mention.menuModules') : t('dock.mention.menuRecords')}
          </p>
          {menuState ? <p className="m-0 px-2 pb-1.5 text-body-sm text-ink-dim">{menuState}</p> : null}
          {options.length ? (
            <ul id={listId} role="listbox" className="m-0 flex list-none flex-col p-0">
              {options.map((option, index) => (
                <li
                  key={`${option.module}:${option.module_id ?? 'all'}`}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={index === highlighted}
                  /* mousedown, not click: a click blurs the field first, and the
                     caret position the insertion depends on goes with it. */
                  onMouseDown={(event) => {
                    event.preventDefault()
                    pick(option)
                  }}
                  onMouseEnter={() => setActive(index)}
                  className={cn(
                    'flex cursor-pointer flex-col rounded-xs px-2 py-1.5',
                    index === highlighted && 'bg-sunken',
                  )}
                >
                  <span className="text-body-sm text-ink">
                    {option.module_id ? '#' : '@'}
                    {option.label}
                  </span>
                  {option.module_id ? (
                    <span className="truncate text-micro font-normal text-ink-dim">
                      {t(`dock.moduleNames.${option.module}`)}
                      {option.detail ? ` · ${option.detail}` : null}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-sm border border-hairline bg-page focus-within:border-forge">
        {attachments}

        {contextLabel ? (
          <div className="flex flex-wrap gap-1.5 px-2.5 pt-2.5" aria-label={t('dock.mention.context')}>
            <span className="inline-flex max-w-full items-center gap-1 rounded-pill border border-subtle bg-card ps-2 pe-0.5 text-body-sm text-ink">
              <span className="truncate">{contextLabel}</span>
              <IconButton
                bare
                label={t('dock.mention.clearContext')}
                onClick={onClearContext}
                className="size-5 shrink-0 rounded-pill max-lg:size-6"
              >
                <Icon name="x" size="xs" />
              </IconButton>
            </span>
          </div>
        ) : null}

        <form className="flex items-center gap-1.5 py-2.5 pe-2 ps-3.5" onSubmit={submit}>
          <label className="sr-only" htmlFor="dock-ask">
            {t('dock.askLabel')}
          </label>
          <input
            id="dock-ask"
            ref={inputRef}
            value={draft}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onClick={(event) => track(event.currentTarget.value, event.currentTarget.selectionStart ?? 0)}
            onBlur={() => setTrigger(null)}
            disabled={disabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={menuOpen && options.length > 0}
            aria-controls={menuOpen && options.length > 0 ? listId : undefined}
            aria-activedescendant={menuOpen && options.length > 0 ? `${listId}-${highlighted}` : undefined}
            aria-autocomplete="list"
            className="min-w-0 flex-1 border-0 bg-transparent text-body font-normal outline-none placeholder:text-ink-dim disabled:opacity-60"
            placeholder={placeholder}
          />
          <button
            type="submit"
            disabled={disabled || !draft.trim()}
            aria-label={t('common:actions.send')}
            className="flex size-8 shrink-0 items-center justify-center rounded-xs bg-forge text-white transition-[filter] hover:brightness-[1.08] disabled:opacity-40"
          >
            <Icon name="send" size="sm" />
          </button>
        </form>
      </div>
    </div>
  )
}
